import React from "react";
import { motion } from "framer-motion";
import { SERVICES } from "@/constants/testIds";

const services = [
    {
        slug: "graphic-design",
        title: "Graphic Design",
        text: "Editorial-grade craft applied to commercial outcomes — brochures, campaign kits, print and considered digital collateral where clarity beats decoration, and results follow.",
    },
    {
        slug: "youtube-design",
        title: "YouTube Design",
        text: "High-clarity thumbnails and channel systems built around a creator's identity — bold typography, sharp contrast, emotion-first faces. Designed for the algorithm, without letting it design us.",
    },
    {
        slug: "social-editorial",
        title: "Social & Editorial",
        text: "Instagram posts, carousels, story sets and campaign creatives designed with hierarchy and rhythm. Content that holds attention beyond the first frame, and sits inside a real system.",
    },
    {
        slug: "marketing-campaigns",
        title: "Marketing Campaigns",
        text: "Multi-creative campaigns tied to clear outcomes and a single voice. Landing pages, brochures and social kits assembled around one narrative, tested for conversion.",
    },
    {
        slug: "community-systems",
        title: "Community Systems",
        text: "Discord builds, moderation frameworks, custom bots and growth playbooks. The invisible architecture that keeps a community healthy — and lets it scale past 90,000 members without breaking.",
    },
    {
        slug: "brand-systems",
        title: "Brand Systems",
        text: "Visual language, design libraries and guidelines engineered for teams to build with, not merely admire. Frameworks that hold their shape as an organisation scales.",
    },
];

export default function Services() {
    return (
        <section
            id="services"
            data-testid={SERVICES.section}
            className="py-28 md:py-44 border-t border-[#E6E2D6]"
        >
            <div className="mx-auto max-w-[1800px] px-6 md:px-12 lg:px-20">
                <div className="grid grid-cols-12 gap-6 md:gap-10 mb-20 md:mb-28">
                    <div className="col-span-12 md:col-span-3">
                        <p className="eyebrow text-[#1B1B1B]/70">
                            § Services
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-9">
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{
                                duration: 1,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="font-serif-editorial text-5xl md:text-7xl lg:text-[104px] leading-[0.98] tracking-[-0.02em] text-[#1B1B1B] max-w-5xl"
                        >
                            A practice built around six disciplines —{" "}
                            <em className="italic text-[#A8844F]">
                                one philosophy.
                            </em>
                        </motion.h2>
                    </div>
                </div>

                <div className="border-t border-[#E6E2D6]">
                    {services.map((s, i) => (
                        <motion.div
                            key={s.slug}
                            data-testid={SERVICES.item(s.slug)}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10%" }}
                            transition={{
                                duration: 0.9,
                                delay: i * 0.04,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="group grid grid-cols-12 gap-6 md:gap-10 py-10 md:py-16 border-b border-[#E6E2D6] cursor-default"
                        >
                            <div className="col-span-2 md:col-span-1">
                                <span className="font-sans-tight text-xs tracking-[0.22em] text-[#1B1B1B]/60 mt-3 inline-block">
                                    /0{i + 1}
                                </span>
                            </div>
                            <div className="col-span-10 md:col-span-5 flex items-start">
                                <h3 className="font-serif-editorial text-3xl md:text-5xl lg:text-6xl text-[#1B1B1B] leading-[1.02] transition-colors duration-500 group-hover:text-[#A8844F]">
                                    {s.title}
                                </h3>
                            </div>
                            <div className="col-span-12 md:col-span-5 md:col-start-8">
                                <p className="font-sans-tight text-base md:text-lg leading-[1.7] text-[#1B1B1B]/85 max-w-md font-light">
                                    {s.text}
                                </p>
                            </div>
                            <div className="hidden md:flex col-span-1 items-center justify-end">
                                <span className="w-8 h-px bg-[#1B1B1B]/30 transition-all duration-500 group-hover:bg-[#A8844F] group-hover:w-12" />
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
